import {
  type ChartAggregation,
  type ChartFormat,
  CORE_CONSTANT,
  type KpiChartDisplaySettingPresetInput,
  type KpiChartDisplaySettingPresetSettings,
  kpiChartDisplaySettingPresetSettingsSchema,
  type ViewConfigDimension,
} from '@salescore/core'
import { stableStringify } from '@salescore/features'

import {
  type ChartDesignSettings,
  DEFAULT_CHART_DESIGN_SETTINGS,
  type KpiDisplayFormat,
} from '../components/view_ui/ViewUIKpi'
import { comboAggregationToRows, rowsToComboAggregation } from '../hooks/useComboChartAggregation'
import { pieAggregationToRows, rowsToPieAggregation } from '../hooks/usePieChartAggregation'

export interface KpiChartDisplaySettingPresetsEntry {
  design: ChartDesignSettings
  aggregation: ChartAggregation
  /** 保存済み状態（差分検知・破棄用）。未保存ならundefined */
  saved?: {
    design: ChartDesignSettings
    aggregation: ChartAggregation
  }
}

export type KpiChartDisplaySettingPresetsState = Record<ChartFormat, KpiChartDisplaySettingPresetsEntry>

// --- Helper functions ---

export function resolveOriginalViewId(viewId: string): string {
  return viewId
    .replace(CORE_CONSTANT.KPI_PREVIEW_VIEW_ID_PREFIX, '')
    .replace(CORE_CONSTANT.KPI_PREVIEW_DRILL_DOWN_VIEW_ID_PREFIX, '')
}

export function isChartFormat(format: KpiDisplayFormat): format is ChartFormat {
  return format === 'pie' || format === 'combo'
}

function validateDimensionKeys(rows: ViewConfigDimension[], availableDimensions: ViewConfigDimension[]): boolean {
  return rows.every((row) => availableDimensions.some((d) => d.key === row.key))
}

export function rowsToAggregation(rows: ViewConfigDimension[], chartFormat: ChartFormat): ChartAggregation | undefined {
  if (chartFormat === 'pie') {
    return rowsToPieAggregation(rows)
  }
  return rowsToComboAggregation(rows)
}

export function aggregationToRows(aggregation: ChartAggregation): ViewConfigDimension[] {
  if (aggregation.chartFormat === 'pie') {
    return pieAggregationToRows(aggregation)
  }
  return comboAggregationToRows(aggregation)
}

export function checkChartDisplaySettingChanges(
  entry: KpiChartDisplaySettingPresetsEntry,
  canUpdate: boolean,
): { hasChanges: boolean; canSave: boolean; canRevert: boolean } {
  const { saved } = entry
  const hasChanges =
    saved === undefined
      ? true
      : stableStringify(entry.design) !== stableStringify(saved.design) ||
        stableStringify(entry.aggregation) !== stableStringify(saved.aggregation)
  return {
    hasChanges,
    canSave: hasChanges && canUpdate,
    canRevert: saved !== undefined && hasChanges && canUpdate,
  }
}

function settingsToAggregation(settings: KpiChartDisplaySettingPresetSettings): ChartAggregation {
  if (settings.chartFormat === 'pie') {
    return { chartFormat: 'pie', breakdown: settings.aggregation.breakdown, category: settings.aggregation.category }
  }
  return { chartFormat: 'combo', xAxis: settings.aggregation.xAxis, breakdown: settings.aggregation.breakdown }
}

function presetSettingsToDesign(settings: KpiChartDisplaySettingPresetSettings): ChartDesignSettings {
  const { design } = settings
  return {
    chartType: 'chartType' in design && design.chartType !== undefined ? design.chartType : 'bar',
    chartValueLabelDisplayMode: design.chartValueLabelDisplayMode ?? 'auto',
    chartValueLabelUnitType: design.chartValueLabelUnitType ?? 'noUnit',
    chartValueLabelDecimalPlaceType: design.chartValueLabelDecimalPlaceType ?? 'automatic',
    chartValueLabelShouldShowPercentage:
      'chartValueLabelShouldShowPercentage' in design && design.chartValueLabelShouldShowPercentage !== undefined
        ? design.chartValueLabelShouldShowPercentage
        : false,
    chartAxisLabelDisplayMode:
      'chartAxisLabelDisplayMode' in design && design.chartAxisLabelDisplayMode !== undefined
        ? design.chartAxisLabelDisplayMode
        : 'auto',
    chartLegendsDisplayMode: design.chartLegendsDisplayMode ?? 'auto',
  }
}

function designToPresetDesign(
  design: ChartDesignSettings,
  chartFormat: ChartFormat,
): KpiChartDisplaySettingPresetInput['design'] {
  if (chartFormat === 'pie') {
    return {
      chartValueLabelDisplayMode: design.chartValueLabelDisplayMode,
      chartValueLabelUnitType: design.chartValueLabelUnitType,
      chartValueLabelDecimalPlaceType: design.chartValueLabelDecimalPlaceType,
      chartLegendsDisplayMode: design.chartLegendsDisplayMode,
    }
  }
  return {
    chartType: design.chartType,
    chartValueLabelDisplayMode: design.chartValueLabelDisplayMode,
    chartValueLabelUnitType: design.chartValueLabelUnitType,
    chartValueLabelDecimalPlaceType: design.chartValueLabelDecimalPlaceType,
    chartValueLabelShouldShowPercentage: design.chartValueLabelShouldShowPercentage,
    chartAxisLabelDisplayMode: design.chartAxisLabelDisplayMode,
    chartLegendsDisplayMode: design.chartLegendsDisplayMode,
  }
}

export function buildPresetInput(
  format: ChartFormat,
  design: ChartDesignSettings,
  aggregation: ChartAggregation,
): KpiChartDisplaySettingPresetInput {
  const presetDesign = designToPresetDesign(design, format)
  return aggregation.chartFormat === 'pie'
    ? {
        chartFormat: aggregation.chartFormat,
        aggregation: { breakdown: aggregation.breakdown, category: aggregation.category },
        design: presetDesign,
      }
    : {
        chartFormat: aggregation.chartFormat,
        aggregation: { xAxis: aggregation.xAxis, breakdown: aggregation.breakdown },
        design: presetDesign,
      }
}

/** presetsからKpiChartDisplaySettingPresetsStateの初期値を構築。preset未保存のフォーマットにもデフォルトaggregationを設定する */
export function buildInitialKpiChartDisplaySettingPresetsState(
  presets: ReadonlyArray<{ setting: unknown }>,
  availableDimensions: ViewConfigDimension[],
  currentPivotRows: ViewConfigDimension[],
): KpiChartDisplaySettingPresetsState {
  const findPreset = (chartFormat: ChartFormat): KpiChartDisplaySettingPresetSettings | undefined => {
    for (const preset of presets) {
      const result = kpiChartDisplaySettingPresetSettingsSchema.safeParse(preset.setting)
      if (!result.success) continue
      if (result.data.chartFormat === chartFormat) return result.data
    }
    return undefined
  }

  return {
    pie: buildEntryForFormat('pie', findPreset('pie'), availableDimensions, currentPivotRows),
    combo: buildEntryForFormat('combo', findPreset('combo'), availableDimensions, currentPivotRows),
  }
}

function buildEntryForFormat(
  chartFormat: ChartFormat,
  preset: KpiChartDisplaySettingPresetSettings | undefined,
  availableDimensions: ViewConfigDimension[],
  currentPivotRows: ViewConfigDimension[],
): KpiChartDisplaySettingPresetsEntry {
  if (preset === undefined) {
    return {
      design: DEFAULT_CHART_DESIGN_SETTINGS,
      aggregation: buildDefaultAggregation(chartFormat, currentPivotRows),
    }
  }

  const design = presetSettingsToDesign(preset)
  const aggregation = settingsToAggregation(preset)
  const rows = aggregationToRows(aggregation)
  const dimensionsValid = availableDimensions.length === 0 || validateDimensionKeys(rows, availableDimensions)

  if (dimensionsValid) {
    return { design, aggregation, saved: { design, aggregation } }
  }
  // dimensionが無効な場合はsavedを設定しない（未保存扱い）
  // 集計軸はデフォルトに差し替え、デザイン設定はそのまま適用
  return { design, aggregation: buildDefaultAggregation(chartFormat, currentPivotRows) }
}

function buildDefaultAggregation(chartFormat: ChartFormat, currentPivotRows: ViewConfigDimension[]): ChartAggregation {
  const rows = currentPivotRows.length > 0 ? currentPivotRows : CORE_CONSTANT.PIVOT_EMPTY_PRESET().parameter.pivot.rows
  const aggregation = rowsToAggregation(rows.slice(0, 2), chartFormat)
  if (aggregation === undefined) {
    throw new Error('currentPivotRows must have at least one element')
  }
  return aggregation
}
